import { useEffect, useMemo, useState, useCallback } from "react"
import {
    X,
    BookOpen,
    Trash2,
    LoaderCircle,
    Plus,
    Search,
    CheckSquare,
    Square,
    GripVertical,
} from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import ConfirmModal from "@/widgets/ConfirmModal"
import {
    getCourseContents,
    unmapCourseContents,
    mapCourseContents,
    reorderCourseContents,
    getSubjects,
} from "@/api/kuizy"
import { EXAM_STATUS } from "@/settings/master"
import ContentDetailModal from "./ContentDetailModal"

// ===== DnD (dnd-kit) =====
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

function SortableRow({ id, children, className = "" }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
    const style = { transform: CSS.Transform.toString(transform), transition }
    return (
        <tr ref={setNodeRef} style={style} className={`${className} ${isDragging ? "opacity-60 ring-1 ring-primary" : ""}`}>
            {/* first cell is handle */}
            <td className="py-2 px-2 w-10 align-top">
                <button className="cursor-grab text-gray-500 hover:text-primary" {...attributes} {...listeners} title="ลากเพื่อจัดลำดับ">
                    <GripVertical size={16} />
                </button>
            </td>
            {children}
        </tr>
    )
}

// ====== Contents Picker (Add contents to course) ======
function ContentsPickerModal({ courseId, currentIds, onSuccess, onClose }) {
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchIn, setSearchIn] = useState("")
    const [search, setSearch] = useState("")
    const [selected, setSelected] = useState({})
    const [saving, setSaving] = useState(false)

    const load = useCallback(() => {
        setLoading(true)
        getSubjects()
            .then(r => {
                const data = Array.isArray(r.data) ? r.data : r.data?.data ?? []
                // Filter out already added contents 
                // Assuming currentIds contains 'id' of contents already in course
                setRows(data)
                setSelected({})
            })
            .finally(() => setLoading(false))
    }, [courseId, currentIds])
    useEffect(load, [load])

    const filteredRows = useMemo(() => {
        let res = rows
        // Filter out already added
        if (currentIds && currentIds.length > 0) {
            res = res.filter(c => !currentIds.includes(c.id))
        }

        if (!search) return res
        const s = search.toLowerCase()
        return res.filter(r =>
            (r.content_name || r.name || "").toLowerCase().includes(s) ||
            (r.content_code || r.code || "").toLowerCase().includes(s)
        )
    }, [rows, search, currentIds])

    const idsOnPage = useMemo(() => filteredRows.map(t => t.id), [filteredRows])
    const allSelected = filteredRows.length > 0 && idsOnPage.every(id => selected[id])
    const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]), [selected])
    const selectedCount = selectedIds.length

    const toggleOne = id => setSelected(s => (s[id] ? { ...s, [id]: undefined } : { ...s, [id]: true }))
    const toggleAll = () =>
        setSelected(s => {
            if (allSelected) {
                const n = { ...s }
                idsOnPage.forEach(id => delete n[id])
                return n
            } else {
                const n = { ...s }
                idsOnPage.forEach(id => (n[id] = true))
                return n
            }
        })

    const save = async () => {
        if (selectedIds.length === 0) return
        setSaving(true)
        try {
            await mapCourseContents(courseId, selectedIds)
            onSuccess && onSuccess()
        } finally {
            setSaving(false)
        }
    }

    return (
        <motion.div key="cpBack" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4">
            <motion.div key="cpCard" initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 28, opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full sm:h-[90vh] sm:w-[90%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
                <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
                    <h2 className="font-semibold text-sm sm:text-base">เลือกเนื้อหา</h2>
                    <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={20} /></button>
                </header>

                <div className="flex flex-wrap items-end gap-3 px-4 py-2 bg-info border-b border-gray-200">
                    <div className="relative flex-1 min-w-[220px]">
                        <label className="flex items-center gap-1 text-sm mb-1"><Search size={16} /> ค้นหา</label>
                        <Search size={16} className="absolute left-3 top-[42px] -translate-y-1/2 text-gray-500 pointer-events-none" />
                        <div className="flex items-center gap-2">
                            <input value={searchIn} onChange={e => setSearchIn(e.target.value)} onKeyDown={e => e.key === "Enter" && setSearch(searchIn)} placeholder="ค้นหาชื่อเนื้อหา / รหัส" className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                            <button onClick={() => setSearch(searchIn)} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"><Search size={16} /> ค้นหา</button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <table className="min-w-[700px] w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="py-2 px-3 w-12 text-left">
                                    <button onClick={toggleAll} className="text-primary hover:text-primary/80" disabled={filteredRows.length === 0}>
                                        {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                                    </button>
                                </th>
                                <th className="py-2 px-3 text-left">รหัสเนื้อหา</th>
                                <th className="py-2 px-3 text-left">ชื่อเนื้อหา</th>
                                <th className="py-2 px-3 text-left">รายละเอียด</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={4} className="text-center py-8"><LoaderCircle className="animate-spin mx-auto" /></td></tr>
                            ) : filteredRows.length === 0 ? (
                                <tr><td colSpan={4} className="text-center py-8 text-gray-500">– ไม่มีข้อมูล –</td></tr>
                            ) : (
                                filteredRows.map(t => {
                                    const id = t.id
                                    const checked = !!selected[id]
                                    return (
                                        <tr key={id} onClick={() => toggleOne(id)} className={`cursor-pointer ${checked ? "bg-primary/10" : "odd:bg-white even:bg-gray-50"} whitespace-nowrap`}>
                                            <td className="py-2 px-3 text-center">{checked ? <CheckSquare size={16} /> : <Square size={16} />}</td>
                                            <td className="py-1.5 px-3">{t.content_code || t.code || "-"}</td>
                                            <td className="py-1.5 px-3 font-medium">{t.content_name || t.name || "-"}</td>
                                            <td className="py-1.5 px-3">{t.description || "-"}</td>
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-600">เลือกแล้ว {selectedCount} รายการ</div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">ยกเลิก</button>
                        <button onClick={save} disabled={selectedCount === 0 || saving} className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
                            {saving && <LoaderCircle size={16} className="animate-spin" />} <Plus size={16} /> เพิ่มเนื้อหา
                        </button>
                    </div>
                </div>
            </motion.div>

        </motion.div>
    )
}

// ===== Main Modal =====
export default function CourseContentModal({ courseId, courseName, onClose }) {
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [picker, setPicker] = useState(false)
    const [alert, setAlert] = useState({ open: false, msg: "" })
    const [delItem, setDelItem] = useState(null)
    const [deleting, setDeleting] = useState(false)

    const [searchIn, setSearchIn] = useState("")
    const [search, setSearch] = useState("")

    const [detailVisible, setDetailVisible] = useState(false)
    const [selectedContent, setSelectedContent] = useState(null)

    const openDetail = (item) => {
        setSelectedContent(item)
        setDetailVisible(true)
    }

    const load = useCallback(() => {
        setLoading(true)
        getCourseContents(courseId)
            .then(r => {
                const data = Array.isArray(r.data) ? r.data : r.data?.data ?? []
                setRows(data)
            })
            .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
            .finally(() => setLoading(false))
    }, [courseId])
    useEffect(load, [load])

    // search
    const list = useMemo(() => {
        const t = search.trim().toLowerCase()
        if (!t) return rows
        return rows.filter(
            x =>
                (x.content_name || x.name || "").toLowerCase().includes(t) ||
                (x.content_code || x.code || "").toLowerCase().includes(t)
        )
    }, [rows, search])

    // DnD ordering
    const ids = useMemo(() => list.map(x => x.id), [list]) // Assuming 'id' is available
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

    const onDragEnd = async evt => {
        const { active, over } = evt
        if (!over || active.id === over.id) return
        const oldIndex = ids.indexOf(active.id)
        const newIndex = ids.indexOf(over.id)

        // Optimistic update
        const newRows = arrayMove(rows, oldIndex, newIndex)
        setRows(newRows)

        try {
            const orderedContentIds = newRows.map(r => r.content_id || r.id)
            await reorderCourseContents(courseId, orderedContentIds)
        } catch {
            load() // Revert on failure
        }
    }

    const confirmUnmapOne = async () => {
        if (!delItem) return
        setDeleting(true)
        try {
            // Assuming reponse of getCourseContents has 'content_id' or 'id'
            // If it's a join table, it might have 'id' (mapping id) and 'content_id'.
            // Usually map APIs expect content_ids to unmap.
            // Let's assume we send content ID.
            await unmapCourseContents(courseId, [delItem.content_id || delItem.id])
            setDelItem(null)
            load()
        } catch {
            setAlert({ open: true, msg: "ไม่สามารถนำออกได้" })
        } finally {
            setDeleting(false)
        }
    }

    return (
        <AnimatePresence>
            <motion.div
                key="ccBack"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4"
            >
                <motion.div
                    key="ccCard"
                    initial={{ y: 32, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 32, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="w-full h-full sm:h-[95vh] sm:w-[95%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden"
                >
                    <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
                        <h2 className="flex items-center gap-2 text-sm sm:text-base font-semibold">
                            <BookOpen size={18} /> เนื้อหาของ “{courseName}” ({list.length})
                        </h2>
                        <button onClick={onClose} className="text-gray-600 hover:text-primary">
                            <X size={20} />
                        </button>
                    </header>

                    <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                        <button onClick={() => setPicker(true)} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white text-sm rounded-lg px-3 py-1">
                            <Plus size={14} /> เพิ่มเนื้อหา
                        </button>
                        <div className="ml-auto flex items-center gap-2">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                                <input
                                    value={searchIn}
                                    onChange={e => setSearchIn(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && setSearch(searchIn)}
                                    placeholder="ค้นหาชื่อเนื้อหา / รหัส"
                                    className="w-[260px] border border-gray-300 rounded-lg pl-9 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary"
                                />
                            </div>
                            <button onClick={() => setSearch(searchIn)} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-1.5 text-sm">
                                <Search size={16} /> ค้นหา
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto">
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                                <table className="min-w-[800px] w-full text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="py-2 px-2 w-10 text-left"></th>
                                            <th className="py-2 px-3 text-left">ชื่อเนื้อหา</th>
                                            <th className="py-2 px-3 text-left">ชื่อย่อเนื้อหา</th>
                                            <th className="py-2 px-3 text-left">สถานะ</th>
                                            <th className="py-2 px-3 w-16 text-right">ลบ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={5} className="text-center py-8">
                                                    <LoaderCircle className="animate-spin mx-auto" />
                                                </td>
                                            </tr>
                                        ) : list.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="text-center py-8 text-gray-500">
                                                    – ไม่มีข้อมูล –
                                                </td>
                                            </tr>
                                        ) : (
                                            list.map(r => (
                                                <SortableRow key={r.id} id={r.id} className="odd:bg-white even:bg-gray-50 align-top">
                                                    {/* Draggable handle is in SortableRow */}
                                                    <td className="py-1.5 px-3 font-medium">
                                                        <button onClick={() => openDetail(r)} className="text-primary hover:underline font-medium text-left">
                                                            {r.content_name || r.name || "-"}
                                                        </button>
                                                    </td>
                                                    <td className="py-1.5 px-3">{r.content_code || r.code || "-"}</td>
                                                    <td className="py-1.5 px-3">
                                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${EXAM_STATUS[r.approval_status]?.style || 'bg-gray-100'}`}>
                                                            {EXAM_STATUS[r.approval_status]?.label || 'Unknown'}
                                                        </span>
                                                    </td>
                                                    <td className="py-1.5 px-3 text-right">
                                                        <button onClick={() => setDelItem(r)} className="text-red-600 hover:text-red-800" title="นำออก">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </td>
                                                </SortableRow>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </SortableContext>
                        </DndContext>
                    </div>

                    <AnimatePresence>
                        {picker && (
                            <ContentsPickerModal
                                courseId={courseId}
                                currentIds={rows.map(r => r.content_id || r.id)}
                                onSuccess={() => {
                                    setPicker(false)
                                    load()
                                }}
                                onClose={() => setPicker(false)}
                            />
                        )}
                    </AnimatePresence>

                    {delItem && (
                        <ConfirmModal
                            type="confirm"
                            title="ยืนยันการลบ"
                            message={`ลบเนื้อหา “${delItem.content_name || delItem.name}” ออกจากหลักสูตรนี้ ?`}
                            pending={deleting}
                            onConfirm={confirmUnmapOne}
                            onCancel={() => setDelItem(null)}
                        />
                    )}
                    {alert.open && (
                        <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: "" })} />
                    )}

                    <ContentDetailModal
                        visible={detailVisible}
                        content={selectedContent}
                        onClose={() => { setDetailVisible(false); setSelectedContent(null) }}
                        onUpdate={load}
                    />
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}
